<script setup lang="ts">
import { useBrands } from '~/data/brands';
import { useDrops } from '~/data/drops';

import NoData from '~/components/new-arrivals/NoData.vue';
import Skeleton from '~/components/new-arrivals/Skeleton.vue';
import SelectPeriod from '~/components/new-arrivals/SelectPeriod.vue';
import NewArrivalsProductList from '~/components/new-arrivals/NewArrivalsProductList.vue';

import EntityCardV2 from '~/components/homepage/octane/EntityCardV2.vue';
import EntityCardV3 from '~/components/homepage/octane/EntityCardV3.vue';

import type { DynamicInputData, EntityData } from '@laam/cms-shared';
import { useStatsigExperiment } from '~/utils/use-statsig-experiment';
import { EntityCarouselLarge, EntityCarouselSmall } from '@laam/ui/cms';

const dropsMetadata: EntityData = {
	alignment: 'left',
	title: 'New Collections',
	entity: 'drops',
	method: 'static',
	input: {
		ids: [],
	},
};

const brandsMetadata: EntityData = {
	alignment: 'left',
	title: 'New Brands',
	entity: 'brands',
	method: 'static',
	input: {
		ids: [],
	},
};

const store = useGlobalStore();
const { storeType } = storeToRefs(store);
const { deviceType } = useDeviceType();
const { newArrivalPageExperiment } = useStatsigExperiment();

const dropsPeriod = ref(store.addOns?.new_arrivals_drops_period || '7d');
const brandsPeriod = ref(store.addOns?.new_arrivals_brands_period || '30d');

const dropsDynamicInputData = computed(() => ({
	timePeriod: dropsPeriod.value,
	limit: 10,
	ranking: 'RECOMMENDED',
	selectedBrands: [],
})) as Ref<DynamicInputData>;

const brandsDynamicInputData = computed(() => ({
	limit: 10,
	ranking: 'RECOMMENDED',
	timePeriod: brandsPeriod.value,
	selectedBrands: [],
})) as Ref<DynamicInputData>;

provide('storeType', storeType);
provide('deviceType', deviceType);

const {
	data: brands,
	waitForBrands,
	isLoading: isLoadingBrands,
} = useBrands({
	enabled: ref(true),
	filters: computed(() => ({
		...brandsDynamicInputData.value,
		created_at: newArrivalPageExperiment?.value?.brand_created_at ?? '30d',
		min_sellable_products:
			newArrivalPageExperiment?.value?.min_sellable_products ?? 5,
	})),
});

const {
	data: drops,
	waitForDrops,
	isLoading: isLoadingDrops,
} = useDrops({
	enabled: ref(true),
	filters: computed(() => ({
		...dropsDynamicInputData.value,
		created_at: newArrivalPageExperiment?.value?.drop_created_at ?? '7d',
		min_sellable_products:
			newArrivalPageExperiment?.value?.min_sellable_products ?? 5,
	})),
});

const dedupedDrops = computed(() => {
	if (!brands.value || !drops.value) return [];

	const brandIds = new Set(brands.value.map((brand) => brand.id));
	return drops.value.filter((drop) => !brandIds.has(drop.brand_id));
});

onServerPrefetch(async () => {
	await Promise.all([waitForDrops(), waitForBrands()]);
});

// await Promise.all([waitForDrops(), waitForBrands()]);
</script>
<template>
	<div
		class="homepage--components py-5xl lg:py-8xl gap-3xl lg:gap-9xl flex flex-col"
	>
		<h1 class="visually-hidden">{{ store.store.name }} New Arrivals</h1>
		<div class="h-11xl w-fill lg:!w-[350px] !mx-3xl flex self-center">
			<div
				class="flex justify-center items-center w-full h-full border-2 border-dashed border-black rounded-[10px]"
			>
				<h2 class="text-black text-3xl font-medium">
					NEW ON {{ store.store.name }}
				</h2>
			</div>
		</div>
		<EntityCarouselLarge
			:metadata="dropsMetadata"
			:section-index="0"
			:data="dedupedDrops"
			:loading="isLoadingDrops"
		>
			<template #selectPeriod="{ entity }">
				<SelectPeriod v-model:period="dropsPeriod" :entity="entity" />
			</template>
			<template #default="{ entity, data: entityData, horizontalIndex }">
				<EntityCardV2
					:entity="entity"
					:data="entityData"
					:horizontal-index="horizontalIndex"
					:component-info="{
						heading: dropsMetadata.title,
						component: 'entity-carousel-v2',
						method: 'dynamic',
						input: dropsDynamicInputData,
					}"
					:data-source="{
						clickedFrom: 'new-arrivals',
						dataSource: 'valhalla',
						version: '1.0.0',
					}"
					:vertical-index="0"
				/>
			</template>
			<template #skeleton>
				<Skeleton />
			</template>
			<template #noData="{ entity }">
				<NoData :entity="entity" :period="dropsPeriod" />
			</template>
		</EntityCarouselLarge>
		<EntityCarouselSmall
			v-if="brands"
			:metadata="brandsMetadata"
			:section-index="1"
			:data="brands"
			:loading="isLoadingBrands"
		>
			<template #selectPeriod="{ entity }">
				<SelectPeriod v-model:period="brandsPeriod" :entity="entity" />
			</template>
			<template #default="{ entity, data: entityData, horizontalIndex }">
				<EntityCardV3
					:entity="entity"
					:data="entityData"
					:horizontal-index="horizontalIndex"
					:component-info="{
						component: 'entity-carousel-v3',
						heading: brandsMetadata.title,
						input: brandsDynamicInputData,
						method: 'dynamic',
					}"
					:data-source="{
						clickedFrom: 'new-arrivals',
						dataSource: 'valhalla',
						version: '1.0.0',
					}"
					:vertical-index="1"
				/>
			</template>
			<template #skeleton>
				<Skeleton />
			</template>
			<template #noData="{ entity }">
				<NoData :entity="entity" :period="brandsPeriod" />
			</template>
		</EntityCarouselSmall>
		<ClientOnly>
			<NewArrivalsProductList />
		</ClientOnly>
	</div>
</template>
<style>
.visually-hidden {
	position: absolute;
	clip: rect(1px, 1px, 1px, 1px);
	-webkit-clip-path: inset(0px 0px 99.9% 99.9%);
	clip-path: inset(0px 0px 9% 9%);
	overflow: hidden;
	height: 1px;
	width: 1px;
	padding: 0;
	border: 0;
}

.w-fill {
	width: -webkit-fill-available;
	width: stretch;
}
</style>
